import { useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DraftStatus from "../../constants/draftStatus";
import Permissions from "../../constants/permissions";
import useAuth from "../../context/UserContext";
import { PublishedDiffOpenFilesDocument, QueryChangelistFileDocument } from "../../graphql/types";
import type {
  PublishedDiffOpenFilesQuery,
  PublishedDiffOpenFilesQueryVariables,
  QueryChangelistFileQuery,
  QueryChangelistFileQueryVariables,
} from "../../graphql/types";
import { AppError } from "../../pages/static/AppError";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import { EditorTabVariant } from "../../store/editor/editorStore";
import { DataLoader, ErrorContainer, LoadingContainer, SuccessContainer } from "../DataLoader";
import CodeEditor from "../MonacoEditor/CodeEditor";
import DiffEditor from "../MonacoEditor/DiffEditor";
import { createSBSMarkers } from "../MonacoEditor/utils";
import { hasPermission } from "../Permissions";
import { LoadingIcon } from "../ui/LoadingIcon";
import { EditorEmptyState } from "./EditorEmptyState";

export const MonacoCodeEditor = ({
  height,
  width,
  panelHeight,
}: {
  height: string | number;
  width: string | number;
  panelHeight: number;
}) => {
  const currentOpenFile = useEditorStoreContext((state) => state.currentOpenFile);
  const openFileTab = useEditorStoreContext((state) => state.openFileTab);

  const { path, variant, originalPath } = currentOpenFile || {
    path: "",
    originalPath: "",
    variant: EditorTabVariant.Editor,
  };
  const currentFile = useEditorStoreContext((state) => state.files.find((f) => f.path === state.currentOpenFile?.path));

  const allFiles = useEditorStoreContext((state) => state.files);

  const currentFilePath = originalPath || path;

  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const hasEdits = currentFile?.editFlags?.updated;

  const readOnly =
    useEditorStoreContext((state) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(state.status)) ||
    !hasPermission(Permissions.DevelopScripts, user) ||
    variant === EditorTabVariant.ReadonlyDiff;

  const setFileText = useEditorStoreContext((state) => state.setFileText);
  const { version_id } = useParams();

  const {
    loading,
    error,
    data: fileData,
  } = useQuery<QueryChangelistFileQuery, QueryChangelistFileQueryVariables>(QueryChangelistFileDocument, {
    skip: !currentFilePath || !!hasEdits || variant !== EditorTabVariant.Editor,
    variables: { id: version_id, opened_paths: [currentFilePath] },
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: diffLoading,
    error: diffError,
    data: diffFileData,
  } = useQuery<PublishedDiffOpenFilesQuery, PublishedDiffOpenFilesQueryVariables>(PublishedDiffOpenFilesDocument, {
    skip:
      !currentFilePath ||
      !!hasEdits ||
      (variant !== EditorTabVariant.EditableDiff && variant !== EditorTabVariant.ReadonlyDiff),
    variables: {
      draft_id: version_id || "",
      draft_opened_paths: [currentFilePath],
      published_opened_paths: [currentFilePath],
    },
    errorPolicy: "all",
    fetchPolicy: "no-cache",
  });

  const compilationMessages = useEditorStoreContext((state) =>
    currentFilePath
      ? state.compilationResult[currentFilePath]?.messages?.filter(
          (msg) => !(msg.type === "beta") || hasPermission(Permissions.BetaMessages, user)
        )
      : null
  );

  useEffect(() => {
    if (!fileData || currentOpenFile?.type !== "file") return;
    const openedFiles = fileData.changelist?.opened_files;
    const text = openedFiles?.length ? openedFiles[0].text : null;
    if (text) setFileText(currentFilePath, text, "server");
  }, [fileData, currentOpenFile?.type, currentFilePath, setFileText]);

  useEffect(() => {
    if (!diffFileData || currentOpenFile?.type !== "file") return;
    const draftOpenedFiles = diffFileData.currentDraft?.opened_files;
    const text = draftOpenedFiles?.length ? draftOpenedFiles[0].text : null;
    if (text) setFileText(currentFilePath, text, "server", "file");
  }, [diffFileData, currentOpenFile?.type, currentFilePath, setFileText]);

  useEffect(() => {
    if (searchParams.has("file")) {
      const filePath = decodeURIComponent(searchParams.get("file") || "");
      const fileType = (searchParams.get("fileType") as "file") || "file";
      const mode = searchParams.get("mode");
      const variant = searchParams.get("variant") || EditorTabVariant.Editor;

      if (fileType === "file") {
        const file = allFiles.find((f) => f.path === filePath);
        if (!file || !mode) return;
        openFileTab(file?.path, fileType, file.editFlags?.originalPath, variant as EditorTabVariant);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  let publishedText = "";
  if (currentOpenFile?.type === "file") {
    publishedText = diffFileData?.publishedDraft?.opened_files?.[0]?.text || "";
  }
  if (!currentFilePath) return <EditorEmptyState />;

  const editorPath = encodeURIComponent(currentFilePath || "");
  return (
    <Grid size={12} sx={{ height: `calc(100% - ${panelHeight}vh - ${50 + 15}px)` }}>
      <DataLoader
        loading={loading || diffLoading}
        error={error || diffError}
        success={currentFile?.text !== undefined || currentFile?.text !== null}
      >
        <LoadingContainer>
          <LoadingIcon />
        </LoadingContainer>
        <ErrorContainer>
          <AppError />
        </ErrorContainer>
        <SuccessContainer>
          {variant === EditorTabVariant.Editor ? (
            <CodeEditor
              value={currentFile?.text || ""}
              language={currentOpenFile?.type === "file" ? "sbs" : "json"}
              path={editorPath}
              height={height}
              width={width}
              position={1}
              onChange={(editorValue) => {
                if (!currentFilePath) {
                  console.error("currentFilePath should not be empty", currentOpenFile);
                }
                setFileText(currentFilePath, editorValue);
              }}
              options={{
                readOnly: readOnly || currentOpenFile?.type !== "file",
              }}
              markers={createSBSMarkers(compilationMessages)}
            />
          ) : (
            <DiffEditor
              modified={currentFile?.text || ""}
              language={currentOpenFile?.type === "file" ? "sbs" : "json"}
              original={publishedText || ""}
              height={height}
              width={width}
              modifiedModelPath={editorPath}
              originalModelPath={`${editorPath}-published`}
              position={1}
              options={{
                readOnly: readOnly,
                readOnlyMessage: undefined,
                originalEditable: true,
              }}
              onChange={(editorValue) => {
                if (!currentFilePath) {
                  console.error("currentFilePath should not be empty", currentOpenFile);
                }
                setFileText(currentFilePath, editorValue);
              }}
              modifiedMarkers={createSBSMarkers(compilationMessages)}
            />
          )}
        </SuccessContainer>
      </DataLoader>
    </Grid>
  );
};
